import React from "react";
import { useTranslation } from "react-i18next";
import { ModuleField } from "../types";
import MappingInput from "./MappingInput";
import HookSelect from "./HookSelect";
import OptionSelect from "../ui/OptionSelect";
import InputSwitch from "../../../components/InputSwitch";

export interface FieldEnv {
    /** Modules that run before this one, for "source module" pickers. */
    upstream: Array<{ id: number; label: string }>;
    /** react-select ignores a disabled fieldset, so selects are told directly. */
    readOnly?: boolean;
}

interface FieldInputProps {
    field: ModuleField;
    value: any;
    onChange: (value: any) => void;
    /** Mapper fields accept {{expressions}}; parameters are fixed settings. */
    mappable: boolean;
    env: FieldEnv;
    invalid?: boolean;
}

const isMapped = (v: any) => typeof v === 'string' && v.includes('{{');


/** One form control, chosen by the field's schema type (Field.php in the cabinet). */
const FieldInput = ({ field, value, onChange, mappable, env, invalid }: FieldInputProps) => {
    switch (field.type) {
        case 'boolean':
            if (mappable && isMapped(value)) {
                return <MappingInput value={value} onChange={onChange} />;
            }

            return (
                <InputSwitch
                    id={'wf-field-' + field.name}
                    value={value === true || value === 'true'}
                    onChange={() => onChange(!(value === true || value === 'true'))}
                    placeholder={field.label}
                    disabled={env.readOnly}
                    className="form-switch-md"
                />
            );

        case 'select':
            if (mappable && isMapped(value)) {
                return <MappingInput value={value} onChange={onChange} />;
            }

            return (
                <OptionSelect options={field.options || []} value={value} required={field.required} invalid={invalid} readOnly={env.readOnly}
                    onChange={(v) => onChange(v ?? '')} />
            );

        case 'hook':
            return <HookSelect value={value ?? null} onChange={onChange} invalid={invalid} />;

        case 'module':
            return (
                <OptionSelect options={env.upstream.map((m) => ({ value: m.id, label: m.id + '. ' + m.label }))} value={value}
                    invalid={invalid} readOnly={env.readOnly} onChange={onChange} />
            );

        case 'array':
            return <ArrayInput field={field} value={value} onChange={onChange} mappable={mappable} env={env} />;

        case 'collection':
            if (field.spec && field.spec.length > 0) {
                const object = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

                return (
                    <div className="border border-dashed rounded p-3 pb-0">
                        {field.spec.map((sub) => (
                            <FieldRow key={sub.name} field={sub} mappable={mappable} env={env}
                                value={object[sub.name]} onChange={(v) => onChange({ ...object, [sub.name]: v })} />
                        ))}
                    </div>
                );
            }

            return <JsonOrMapping value={value} onChange={onChange} />;

        case 'number':
        case 'integer':
            if (!mappable) {
                return <input type="number" className={"form-control" + (invalid ? " is-invalid" : "")} value={value ?? ''}
                    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
            }

            return <MappingInput value={value} invalid={invalid} onChange={(v) => onChange(/^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v)} />;

        case 'any':
        case 'json':
            return <JsonOrMapping value={value} onChange={onChange} />;

        default:
            return mappable
                ? <MappingInput value={value} onChange={onChange} multiline={field.multiline} invalid={invalid} placeholder={field.type === 'url' ? 'https://' : undefined} />
                : <input type="text" className={"form-control" + (invalid ? " is-invalid" : "")} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
    }
};

/** A value that is either a {{mapping}} / text, or structured JSON. */
const JsonOrMapping = ({ value, onChange }: { value: any; onChange: (v: any) => void }) => {
    const [draft, setDraft] = React.useState<string>(typeof value === 'string' ? value : value === undefined || value === null ? '' : JSON.stringify(value, null, 2));
    const [error, setError] = React.useState(false);

    const commit = (text: string) => {
        setDraft(text);
        const trimmed = text.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                onChange(JSON.parse(trimmed));
                setError(false);

                return;
            } catch {
                // Not JSON (yet): keep it as text so {{mappings}} still work.
                setError(!trimmed.includes('{{'));
            }
        } else {
            setError(false);
        }
        onChange(text);
    };

    return <MappingInput value={draft} onChange={commit} multiline invalid={error} />;
};

const ArrayInput = ({ field, value, onChange, mappable, env }: { field: ModuleField; value: any; onChange: (v: any) => void; mappable: boolean; env: FieldEnv }) => {
    const { t } = useTranslation(['workflow']);

    if (typeof value === 'string') {
        return (
            <div>
                <MappingInput value={value} onChange={onChange} />
                <button type="button" className="btn btn-sm btn-ghost-primary mt-2" onClick={() => onChange([])}>{t('workflow:editAsList')}</button>
            </div>
        );
    }

    const items: any[] = Array.isArray(value) ? value : [];
    const spec = field.spec || [];
    const setItem = (index: number, item: any) => onChange(items.map((v, i) => (i === index ? item : v)));

    return (
        <div>
            {items.map((item, index) => (
                <div key={index} className="d-flex gap-2 align-items-start mb-2">
                    <div className="flex-grow-1">
                        {spec.length > 0
                            ? <div className="row g-1">
                                {spec.map((sub) => (
                                    <div key={sub.name} className={spec.length > 1 ? "col" : "col-12"}>
                                        <FieldInput field={sub} value={item?.[sub.name]} mappable={mappable} env={env}
                                            onChange={(v) => setItem(index, { ...(item || {}), [sub.name]: v })} />
                                        {index === 0 && <div className="text-muted fs-12 mt-1">{sub.label}</div>}
                                    </div>
                                ))}
                            </div>
                            : <MappingInput value={item} onChange={(v) => setItem(index, v)} />
                        }
                    </div>
                    <button type="button" className="btn btn-soft-danger btn-icon" onClick={() => onChange(items.filter((_, i) => i !== index))}>
                        <i className="ri-delete-bin-line" />
                    </button>
                </div>
            ))}
            <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-soft-primary" onClick={() => onChange([...items, spec.length > 0 ? {} : ''])}>
                    <i className="ri-add-line align-bottom me-1" />{t('workflow:addItem')}
                </button>
                {mappable &&
                    <button type="button" className="btn btn-sm btn-ghost-primary" onClick={() => onChange('{{}}')}>{t('workflow:mapWholeArray')}</button>
                }
            </div>
        </div>
    );
};

/**
 * A labelled field with its help text, laid out like the app's entity forms (the post editor):
 * form-label above the control, a switch carrying its own label.
 */
export const FieldRow = ({ field, value, onChange, mappable, env, invalid }: FieldInputProps) => {
    const inlineSwitch = field.type === 'boolean' && !(mappable && isMapped(value));

    return (
        <div className="mb-3">
            {!inlineSwitch &&
                <label className="form-label">
                    {field.label}{field.required && <span className="text-danger ms-1">*</span>}
                </label>
            }
            <FieldInput field={field} value={value} onChange={onChange} mappable={mappable} env={env} invalid={invalid} />
            {field.help && <div className="form-text">{field.help}</div>}
        </div>
    );
};

export default FieldInput;
